// Single source of truth for every enum value used by the API (zod), the DB seed and the UI.
// UI labels live in web/src/ui/labels.ts — keep values here machine-friendly and stable.

export const BUSINESS_TYPES = ['restaurant', 'cafe', 'pizzeria', 'bar', 'retail', 'salon', 'other'] as const;
export type BusinessType = (typeof BUSINESS_TYPES)[number];

export const OWNER_ROLES = ['owner', 'manager', 'partner'] as const;
export type OwnerRole = (typeof OWNER_ROLES)[number];

export const LANGUAGES = ['en', 'es', 'pt'] as const;
export type Language = (typeof LANGUAGES)[number];

export const POS_SYSTEMS = [
  'toast',
  'clover',
  'spoton',
  'touchbistro',
  'lightspeed',
  'shopify',
  'chownow',
  'aloha',
  'micros',
  'square',
  'cash_only',
  'other',
  'unknown',
] as const;
export type PosSystem = (typeof POS_SYSTEMS)[number];

export const STAGES = [
  'new',
  'visited',
  'qualified',
  'demo',
  'proposal',
  'closed_won',
  'closed_lost',
] as const;
export type Stage = (typeof STAGES)[number];

export const CLOSED_STAGES: readonly Stage[] = ['closed_won', 'closed_lost'];

export const HEATS = ['hot', 'warm', 'cold'] as const;
export type Heat = (typeof HEATS)[number];

export const SPOKE_WITH = ['owner', 'manager', 'gatekeeper', 'nobody'] as const;
export type SpokeWith = (typeof SPOKE_WITH)[number];

export const OUTCOMES = ['interested', 'follow_up', 'not_interested', 'asked_to_return', 'closed'] as const;
export type Outcome = (typeof OUTCOMES)[number];

export const OBJECTION_TYPES = [
  'price',
  'contract_lock',
  'missing_feature',
  'loyal_to_current_rep',
  'partner_decides',
  'just_switched',
  'other',
] as const;
export type ObjectionType = (typeof OBJECTION_TYPES)[number];

export const FEATURE_STATUSES = ['missing', 'launched'] as const;
export type FeatureStatus = (typeof FEATURE_STATUSES)[number];

export const DRAFT_TRIGGERS = [
  'contract_expiring',
  'feature_launched',
  'post_visit',
  'meeting_confirm',
] as const;
export type DraftTrigger = (typeof DRAFT_TRIGGERS)[number];

export const DRAFT_STATUSES = ['pending', 'copied', 'dismissed'] as const;
export type DraftStatus = (typeof DRAFT_STATUSES)[number];

/** POS detected by the corridor website fingerprint (M2). 'other' is reserved for manual tagging.
 *  `shopify` says the storefront's website runs on Shopify, which in a boutique usually means
 *  Shopify POS at the counter — a tell worth a visit, not proof. `chownow` is not a POS at all: it
 *  says the merchant already pays a separate online-ordering vendor (D95). */
export const DETECTED_POS = [
  'square',
  'toast',
  'clover',
  'spoton',
  'touchbistro',
  'lightspeed',
  'shopify',
  'chownow',
  'other',
  'unknown',
] as const;
export type DetectedPos = (typeof DETECTED_POS)[number];

/** Competitor systems — a detected one makes a corridor pin a hot target (green). */
export const COMPETITOR_POS: readonly DetectedPos[] = [
  'toast',
  'clover',
  'spoton',
  'touchbistro',
  'lightspeed',
  'shopify',
  'chownow',
  'other',
];

/** New-business signals (D57): public records that a business is opening or changing hands. */
export const SIGNAL_SOURCES = [
  'abc',
  'ochca',
  'anaheim',
  'huntington_beach',
  'yorba_linda',
  'la_habra',
  'newport_beach',
  'lake_forest',
  'costa_mesa',
  'laguna_beach',
  'irvine',
  'orange',
  'tustin',
  'fullerton',
  'westminster',
  'brea',
  'cypress',
  'fountain_valley',
  'stanton',
  'seal_beach',
  'san_clemente',
  'los_alamitos',
  'fbn',
  'city_agendas',
  'anaheim_permits',
] as const;
export type SignalSource = (typeof SIGNAL_SOURCES)[number];
/** How a source comes in (D77): the server reads it, the rep's bookmark, an email, or an upload. */
export const SOURCE_MODES = ['server', 'bookmark', 'email', 'upload'] as const;
export type SourceMode = (typeof SOURCE_MODES)[number];
/** What the sheet says of a source (D77). */
export const SOURCE_STATES = ['ok', 'failing', 'red', 'blocked', 'stale', 'waiting', 'off'] as const;
export type SourceState = (typeof SOURCE_STATES)[number];
/** What started an attempt (D77). */
export const SOURCE_TRIGGERS = ['cron', 'run_now', 'import'] as const;
export type SourceTrigger = (typeof SOURCE_TRIGGERS)[number];

/** new_business = a new license or build; new_owner = a transfer (the business was sold); moving = a new
 *  address; remodel = an existing food business rebuilding (new equipment, often a new POS moment). */
export const SIGNAL_EVENTS = ['new_business', 'new_owner', 'moving', 'remodel'] as const;
export type SignalEvent = (typeof SIGNAL_EVENTS)[number];

export const SIGNAL_KINDS = ['food_drink', 'retail', 'food_truck', 'service'] as const;
export type SignalKind = (typeof SIGNAL_KINDS)[number];

export const SIGNAL_STATUSES = ['new', 'added', 'dismissed'] as const;
export type SignalStatus = (typeof SIGNAL_STATUSES)[number];

/** The sorting layer's verdict on a new business (D72): worth a visit, in the queue, hidden with a reason,
 *  or merged into another card for the same place. The rep's own status is separate. */
export const SIGNAL_BUCKETS = ['top', 'queue', 'hidden', 'merged'] as const;
export type SignalBucket = (typeof SIGNAL_BUCKETS)[number];
/** The rep's override of the machine's bucket; it wins forever (D72). */
export const REP_BUCKETS = ['queue', 'hidden'] as const;
export type RepBucket = (typeof REP_BUCKETS)[number];
export const HIDE_REASONS = ['chain', 'outside_oc', 'already_square', 'rep', 'later'] as const;
export type HideReason = (typeof HIDE_REASONS)[number];
export const SCORE_PARTS = [
  'event',
  'kind',
  'google_new',
  'google_open',
  'google_established',
  'google_absent_contact',
  'google_absent',
  'pos',
  'filed',
  'phone',
  'email',
  'website',
  'operator',
] as const;
export type ScorePart = (typeof SCORE_PARTS)[number];
/** What the sort could not settle about a row, said on the card (D75). */
export const TRIAGE_FLAGS = ['city_unverified'] as const;
export type TriageFlag = (typeof TRIAGE_FLAGS)[number];
/** Why the rep dismissed a business, when they said (D72); 'chain' also teaches the chain list. */
export const DISMISS_REASONS = ['chain', 'kind', 'area'] as const;
export type DismissReason = (typeof DISMISS_REASONS)[number];
/** What Google says of a business once asked (D79): open, closed for now, or not listed yet. */
export const GOOGLE_STATUSES = ['open', 'closed_temporarily', 'not_found'] as const;
export type GoogleStatus = (typeof GOOGLE_STATUSES)[number];

/** How a touch happened (D90). `voicemail` is a call nobody picked up — kept apart from `call`
 *  because the funnel reads very differently for the two. Rows logged before D90 are `walk_in`. */
export const TOUCH_CHANNELS = [
  'walk_in',
  'call',
  'voicemail',
  'email',
  'linkedin',
  'meeting',
  'other',
] as const;
export type TouchChannel = (typeof TOUCH_CHANNELS)[number];
/** Standing in front of them. D10's "a visit moves a new lead to visited" means these two only:
 *  a voicemail nobody heard is not a visit, and neither is an email. */
export const IN_PERSON_CHANNELS: readonly TouchChannel[] = ['walk_in', 'meeting'];

// ---------------------------------------------------------------- the message playbook (D88)

/** Who the owner is, in the four kinds the message library is written for (D88). */
export const PERSONAS = ['restaurant', 'retail', 'salon', 'counter'] as const;
export type Persona = (typeof PERSONAS)[number];

/** How a message is delivered. `email` and `linkedin` are copied out of the app and sent by the rep
 *  from his own machine — nothing here ever sends (project guardrail). */
export const PLAYBOOK_CHANNELS = ['email', 'call', 'walk_in', 'linkedin'] as const;
export type PlaybookChannel = (typeof PLAYBOOK_CHANNELS)[number];

/** One message of the library, by what it is for. Not every persona has every one (D88). */
export const PLAYBOOK_VARIANTS = [
  'pre_open',
  'switch',
  'no_hook',
  'pain',
  'voicemail',
  'voicemail_q4',
  'gatekeeper',
  'walk_in_owner',
  'walk_in_owner_q4',
  'walk_in_staff',
  'card_note',
  'linkedin',
  'linkedin_connected',
  'follow_up_2',
  'follow_up_3_pre_open',
  'follow_up_3',
  'follow_up_4',
  'follow_up_5',
] as const;
export type PlaybookVariant = (typeof PLAYBOOK_VARIANTS)[number];

export const PLAN_GAP_REASONS = ['rush', 'opening_hours', 'meeting', 'day_limit'] as const;
export type PlanGapReason = (typeof PLAN_GAP_REASONS)[number];

/** Independent, durable lead coordinates (D105). */
export const COORDINATE_SOURCES = ['census', 'public_roster', 'manual'] as const;
export type CoordinateSource = (typeof COORDINATE_SOURCES)[number];
export const LOCATION_STATUSES = ['unresolved', 'resolving', 'resolved', 'needs_review', 'no_match', 'temporary_error'] as const;
export type LocationStatus = (typeof LOCATION_STATUSES)[number];
